import { BLUE } from '../Theme/palette';
import { OrderVehicleReminder } from '@components';
import { useServiceReminders, useAccess } from '@hooks';
import { orderHasVehicle, OrderStatus } from '@models';
import { CreateServiceHistory } from '@services';
import { useStore } from '@stores';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
  message,
} from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useView } from '@hooks';
import './ServiceHistoryModal.css';

const { Text } = Typography;

interface IProps {
  open: boolean;
  orderId: string;
  mileage: number | undefined;
  vin?: string;
  registrationNumber?: string;
  customerId?: string;
  onCancel: () => void;
  /** Called once the job has been saved as Completed (before the reminders step, if any). */
  onCompleted?: () => void;
  setOpen: (open: boolean) => void;
}

export const ServiceHistoryModal: React.FC<IProps> = observer(
  ({ open, orderId, mileage, vin, registrationNumber, customerId, onCancel, onCompleted, setOpen }) => {
    const { isMobile } = useView();
    const { orderStore, serviceHistoryStore, vehicleStore } = useStore();
    const [form] = Form.useForm();
    const [showRemindersForm, setShowRemindersForm] = React.useState(false);
    const [reminderVin, setReminderVin] = React.useState('');
    const savingRef = useRef(false);

    const { serviceHistoryReminders } = useAccess().order;

    const { reminders, loading } = useServiceReminders();

    // Everything here comes from the job, so completing it never waits on, or fails with, a vehicle
    // lookup. The vehicle's own mileage is updated when the job is invoiced, and only ever upwards.
    // A job with no vehicle (a voided QuickPay sale, say) has no mileage or service history to record.
    // A job with only a plate has its VIN looked up once it is saved, see handleUpdate.
    const hasVehicle = orderHasVehicle({ vin, registrationNumber });

    useEffect(() => {
      form.setFieldsValue({
        mileage,
        orderId,
        status: OrderStatus.Completed,
        vin: vin ?? '',
        registrationNumber: registrationNumber ?? '',
      });
      mileage && form.validateFields(['mileage']);
    }, [form, vin, registrationNumber, mileage, orderId]);

    const handleUpdate = useCallback(
      async (values: CreateServiceHistory) => {
        // The steps below leave gaps with the button live, and a second run would record the history twice.
        if (savingRef.current) return;
        savingRef.current = true;

        try {
          const updateSuccess = await orderStore.updateStatus({
            id: values.orderId,
            mileage: values.mileage,
            status: OrderStatus.Completed,
          });

          if (!updateSuccess) return;

          onCompleted?.();

          if (!hasVehicle) {
            handleCancel();
            return;
          }

          // Service history and reminders are read back by VIN, so a job raised with only a plate
          // takes the one on record. The job is already saved: not finding one costs only these steps.
          const historyVin =
            vin || (registrationNumber && (await vehicleStore.findVinByRegistration(registrationNumber))) || '';

          if (historyVin) {
            const { status, ...historyPayload } = values as any;
            await serviceHistoryStore.create({ ...historyPayload, vin: historyVin });
          } else if (values.reminderServiceIds?.length) {
            // A record with no VIN could never be read back, so it is not written.
            message.warning('Job completed. This vehicle has no VIN on record, so the service history was not saved.');
          }

          // A vehicle with no VIN at all has nowhere to keep reminders.
          if (serviceHistoryReminders && historyVin) {
            setReminderVin(historyVin);
            setShowRemindersForm(true);
          } else {
            handleCancel();
          }
        } finally {
          savingRef.current = false;
        }
      },
      [vin, registrationNumber, hasVehicle, serviceHistoryReminders]
    );

    const handleCancel = () => {
      setOpen(false);
      onCancel();
      setShowRemindersForm(false);
    };

    const footer = !showRemindersForm
      ? (
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button
                loading={orderStore.statusLoading || serviceHistoryStore.loading}
                disabled={orderStore.statusLoading || serviceHistoryStore.loading}
                type="primary"
                onClick={() => form.submit()}
              >
                {hasVehicle ? 'Save & continue' : 'Complete job'}
              </Button>
            </Col>
          </Row>
        )
      : (
          <Row justify="end">
            <Col>
              <Button type="primary" onClick={handleCancel}>
                Complete
              </Button>
            </Col>
          </Row>
        );

    return (
      <Drawer
        destroyOnHidden
        title={showRemindersForm ? 'Update reminders' : hasVehicle ? 'Service history' : 'Complete job'}
        open={open}
        loading={loading || serviceHistoryStore.loading}
        onClose={handleCancel}
        size={isMobile ? '100%' : 1200}
        footer={footer}
      >
        {!showRemindersForm ? (
          <Form
            form={form}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            onFinish={handleUpdate}
          >
            <Form.Item name="orderId" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="status" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="vin" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="registrationNumber" hidden>
              <Input size="small" />
            </Form.Item>
            {hasVehicle && (
              <Card
                size="small"
                style={{ marginBottom: 16, background: '#fafafa' }}
              >
                <Form.Item
                  name="mileage"
                  required
                  style={{ marginBottom: 0 }}
                  rules={[
                    { required: true, message: 'Mileage is required' },
                    {
                      type: 'number',
                      max: 1000000,
                      warningOnly: true,
                      message: `Maximum allowed mileage is ${'1000000'.toLocaleString()}.`,
                      transform: (value) => Number(value),
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    style={{ width: isMobile ? '100%' : 220 }}
                    controls={false}
                    changeOnWheel={false}
                    size="large"
                    placeholder="Enter mileage"
                    prefix={<DashboardOutlined style={{ color: BLUE.badge }} />}
                    formatter={(value) => (value ? `${value}`.replace(/\./g, '') : '')}
                    parser={(value) => (value ? value.replace(/\./g, '') : '')}
                  />
                </Form.Item>
              </Card>
            )}

            <Row gutter={[16, 16]}>
              <Col span={isMobile || !hasVehicle ? 24 : 6}>
                <Card
                  size="small"
                  title="Job details"
                  styles={{ header: { background: '#fafafa' } }}
                  style={{ height: '100%' }}
                >
                  {orderStore.order?.serviceOrders.map((serviceOrder) => (
                    <div key={serviceOrder.id} style={{ marginBottom: 8 }}>
                      <Text strong>{serviceOrder.title}</Text>
                      {serviceOrder.extras.length > 0 && (
                        <div style={{ marginLeft: 12, marginTop: 2 }}>
                          {serviceOrder.extras.map((extra) => (
                            <Text key={extra.id} type="secondary" style={{ display: 'block', fontSize: 13 }}>
                              {extra.name}
                            </Text>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </Card>
              </Col>

              {hasVehicle && (
                <Col span={isMobile ? 24 : 18}>
                  <Card
                    size="small"
                    title="Record service history"
                    styles={{ header: { background: '#fafafa' }, body: { padding: '16px 16px 28px' } }}
                    style={{ height: '100%', overflow: 'hidden' }}
                  >
                    <Form.Item name="reminderServiceIds" style={{ marginBottom: 4 }} className="service-history-checkboxes">
                      <Checkbox.Group style={{ width: '100%' }} disabled={loading}>
                        {Object.keys(reminders).length > 0 ? (
                          <Row gutter={[12, 12]}>
                            {Object.entries(reminders).map(([_key, reminder]) => (
                              <Col key={reminder.id} xs={24} sm={12} md={8}>
                                <div
                                  style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 6,
                                    padding: '10px 12px',
                                    background: '#fff',
                                    height: '100%',
                                  }}
                                >
                                  <div
                                    style={{
                                      background: '#fafafa',
                                      margin: '-10px -12px 8px',
                                      padding: '6px 12px',
                                      borderBottom: '1px solid #f0f0f0',
                                      borderRadius: '6px 6px 0 0',
                                    }}
                                  >
                                    <Text strong style={{ fontSize: 13 }}>{reminder.title}</Text>
                                  </div>
                                  {Object.entries(reminder.reminderServices).map(
                                    ([_reminderServiceId, reminderService]) => (
                                      <div key={reminderService.id} style={{ marginBottom: 2 }}>
                                        <Checkbox value={reminderService.id}>
                                          {reminderService.name}
                                        </Checkbox>
                                      </div>
                                    )
                                  )}
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No service history items available"
                          />
                        )}
                      </Checkbox.Group>
                    </Form.Item>
                  </Card>
                </Col>
              )}
            </Row>
          </Form>
        ) : (
          <OrderVehicleReminder
            vin={reminderVin}
            customerId={customerId || ''}
            registrationNumber={registrationNumber || ''}
            currentMileage={form.getFieldValue('mileage')}
          />
        )}
      </Drawer>
    );
  }
);

